import moment, { Moment } from 'moment';

import { groupByKey } from '../helpers';

const splitReservationDeals = (offers: Array<Offer>, type: OfferListType): Array<OfferGroup> => {
  if (type === 'active') {
    const groupedByTemplate = Object.values(groupByKey(offers, 'template_id'));

    return groupedByTemplate
      .map((item) => {
        const result: Array<OfferGroup> = [];

        //get most recent offer
        const mostRecent = item.reduce((recent, offer) =>
          moment(recent.starts_at).isAfter(moment(offer.starts_at)) ? recent : offer
        );

        // move all attendees from offers with same id as mostRecent to mostRecent, keep other offers
        mostRecent.attendees.push(
          ...item
            .filter((off) => off.id !== mostRecent.id)
            .map((offer) => offer.attendees)
            .flat()
        );

        mostRecent.awaitings = mostRecent.attendees.filter((item) => !item.reserved_for);

        mostRecent.attendees = mostRecent.attendees.filter(
          (att) =>
            att.status !== 'expired' && att.reserved_for && moment().isSame(att.reserved_for, 'day')
        );
        result.push({
          offer: mostRecent,
          parts: { type: 'reservation_slot', offer: mostRecent } as ReservationSlots,
        });

        const bucketAttendees = mostRecent.attendees.filter((att) => att.reserved_for);
        if (bucketAttendees.length)
          result.push({
            offer: mostRecent,
            parts: {
              type: 'reservation_offer',
              attendees: bucketAttendees,
              offerId: mostRecent.id,
            } as ReservationBuckets,
          });
        return result;
      })
      .flat();
  } else if (type === 'upcoming') {
    const result: OfferGroup[] = [];
    const now = moment();
    const rrOffersFromEarlier = offers.filter((offer) =>
      moment(offer.starts_at).isSameOrBefore(now, 'day')
    );

    const attendees = rrOffersFromEarlier
      .map((offer) =>
        (offer.attendees || [])
          .filter(
            (attendee) => attendee.reserved_for && moment(attendee.reserved_for).isAfter(now, 'day')
          )
          .map((attendee) => ({ ...attendee, template_id: offer.template_id, offer_id: offer.id }))
      )
      .flat();

    attendees.forEach((attendee) => {
      if (!attendee.reserved_for) return;
      const offer = offers.find(
        (offer) =>
          moment(offer.starts_at).isSame(moment(attendee.reserved_for), 'day') &&
          offer.template_id === attendee.template_id
      );
      if (offer) {
        if (offer.attendees) offer.attendees.push(attendee);
        else offer.attendees = [attendee];
      } else {
        const fromOffer = offers.find((offer) => offer.id === attendee.offer_id);
        if (!fromOffer) return;
        const newOffer = {
          ...fromOffer,
          starts_at: attendee.reserved_for,
          ends_at: moment(attendee.reserved_for).add(2, 'days').format(),
          attendees: [attendee],
        };
        offers.push(newOffer);
      }
    });

    rrOffersFromEarlier.forEach((offer) => {
      const id = offers.findIndex((off) => off.id === offer.id);
      if (id !== -1) offers.splice(id, 1);
    });

    offers.map((offer) => {
      result.push({ offer, parts: { type: 'reservation_slot', offer } as ReservationSlots });
      if (offer.attendees && offer.attendees.length)
        result.push({
          offer,
          parts: {
            type: 'reservation_offer',
            attendees: offer.attendees,
            offerId: offer.id,
          } as ReservationBuckets,
        });

      return offer;
    });

    return result;
  } else {
    const ghosts: Attendee[] = [];
    const tmp: Offer[] = offers.map((offer) => {
      const attendees = offer.attendees.filter((att) => {
        if (moment(offer.starts_at).isSame(att.reserved_for, 'day')) {
          return att.checked_in_at != null || att.canceled_at != null || att.expires_at;
        } else if (moment(att.reserved_for).isBefore(moment(), 'day')) {
          ghosts.push({ ...att, template_id: offer.template_id });
          return false;
        } else {
          return false;
        }
      });
      return { ...offer, attendees };
    });

    ghosts.forEach((att) => {
      if (!att.reserved_for) return;

      const offer = tmp.findIndex(
        (off) =>
          moment(off.starts_at).isSame(moment(att.reserved_for), 'day') &&
          off.template_id === att.template_id
      );

      if (offer !== -1) {
        if (tmp[offer].attendees) tmp[offer].attendees.push(att);
        else tmp[offer].attendees = [att];
      } else {
        const fromOffer = offers.find((offer) => offer.template_id === att.template_id);
        if (!fromOffer) return;

        const newOffer: Offer = {
          ...fromOffer,
          starts_at: att.reserved_for,
          ends_at: moment(att.reserved_for).add(2, 'days').format(),
          attendees: [att],
        };
        tmp.push(newOffer);
      }
    });

    return tmp
      .map((offer) => {
        const result: Array<OfferGroup> = [];

        if (offer.attendees.length) {
          result.push({
            offer,
            parts: {
              type: 'reservation_offer',
              attendees: offer.attendees,
              offerId: offer.id,
            } as ReservationBuckets,
          });
        }
        return result;
      })
      .flat();
  }
};

const splitGroups = (offers: Array<Offer>): Array<OfferGroup> => {
  return offers.map((offer) => {
    const attendees = offer.attendees;
    const findStatus = (inv) => {
      const claim = attendees.find((at) => at.model.user_id === inv.user_id);
      return claim ? claim.status || inv.status : inv.status;
    };
    const groups = offer.groups
      ? offer.groups.map((group) => ({
          ...group,
          invitees: group.invitees.map((inv) => ({
            ...inv,
            status: findStatus(inv),
            claim: attendees.find((at) => at.model.user_id === inv.user_id),
          })),
          owner: {
            ...group.owner,
            status: findStatus(group.owner),
            claim: attendees.find((at) => at.model.user_id === group.owner.user_id),
          },
        }))
      : undefined;
    const offer2 = { ...offer, groups };

    return { offer: offer2, parts: { type: 'group_offer', offer: offer2 } as GroupOffer };
  });
};

const sortFunction = (desc: boolean) => (a: OfferGroup, b: OfferGroup) => {
  const resvs = ['reservation_slot', 'reservation_offer'];
  if (resvs.includes(a.parts.type) && resvs.includes(a.parts.type)) {
    if (
      a.parts.type !== b.parts.type &&
      moment(a.offer.starts_at).format('MM-DD-YYYY') ===
        moment(b.offer.starts_at).format('MM-DD-YYYY')
    ) {
      return resvs.indexOf(a.parts.type) > resvs.indexOf(b.parts.type) ? -1 : 1;
    }
  }

  return (moment(a.offer.starts_at).isAfter(b.offer.starts_at) ? 1 : -1) * (desc ? -1 : 1);
};

const sortOffers = (offers: Array<OfferGroup>, desc: boolean) => {
  const sortOfferFunction = (a: Offer, b: Offer) => {
    return moment(a.starts_at).isAfter(b.starts_at) ? 1 : -1;
  };
  return offers
    .map((group) => {
      if (group.parts.type === 'normal') {
        group.parts.offers.sort(sortOfferFunction);
      }
      return group;
    })
    .sort(sortFunction(desc));
};

declare interface OfferGroupDict {
  [key: string]: { offer: Offer; parts: NormalOffer };
}

const groupOffersByStuff = (offers: Array<Offer>): Array<OfferGroup> => {
  const offerKey = (offer: Offer) => moment(offer.starts_at).format('MMM-DD') + (offer.template_id ?? offer.subtitle);
  const offerReduce = (value: OfferGroupDict, x: Offer): OfferGroupDict => {
    const key = offerKey(x);
    if (!value[key]) {
      value[key] = { offer: x, parts: { type: 'normal', offers: [x] } };
    } else {
      const tmp: NormalOffer = value[key].parts;
      value[key] = {
        offer: x,
        parts: { type: 'normal', offers: [...tmp.offers, x] },
      };
    }
    return value;
  };
  return Object.values(offers.reduce(offerReduce, {} as any));
};

const correctId = (offer: Offer): Offer => {
  return { ...offer, id: offer.id || offer.template_key };
};

const formatOffers = (offers: Array<Offer>, type: OfferListType): Array<OfferGroup> => {
  const idOffers = offers.map(correctId);
  const normalOffers = idOffers.filter(
    (offer) => offer.type !== 'reservation_required' && !offer.is_group
  );
  const reservationOffers = idOffers.filter((offer) => offer.type === 'reservation_required');
  const groupOffers = idOffers.filter((offer) => offer.is_group);

  return sortOffers(
    [
      ...groupOffersByStuff(normalOffers),
      ...splitReservationDeals(reservationOffers, type),
      ...splitGroups(groupOffers),
    ],
    type === 'past'
  );
};

const setNewOfferTimes = (start_time: Moment, end_time: Moment, now: Moment) => {
  let start = start_time.clone();
  let end = end_time.clone();
  if (start_time.isAfter(now) && end_time.isAfter(now)) {
    //nothing
  } else if (start_time.isBefore(now) && end_time.isAfter(now)) {
    start = start.add(1, 'day');
    end = end.add(1, 'day');
  } else {
    start = start.set({ date: now.date(), month: now.month(), year: now.year() });
    end = end.set({ date: now.date(), month: now.month(), year: now.year() });
    while (start.isBefore(now) && end.isBefore(now)) {
      start.add(1, 'day');
      end.add(1, 'day');
    }
  }
  return { starts_at: start.format(), ends_at: end.format() };
};

export { formatOffers, correctId, setNewOfferTimes };
